import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ShoppingCart, 
  LogOut,
  RefreshCw,
  AlertCircle,
  Plus
} from 'lucide-react';
import { Container } from '@/components/layout/container';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AuthGuard } from '@/features/auth/components/auth-guard';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { 
  updateOrderStatus, 
  assignRider,
  Order,
  OrderItem,
  createAdminOrder,
  deleteOrder,
  getAllOrders,
  updateOrderDetails,
} from '@/features/orders/api/admin-orders';
import { OrderCard, OrderCardAction } from '@/features/orders/components/order-card';
import { isPendingCall } from '@/features/orders/utils/order-display';
import { buildAssignRiderRequest, type AssignRiderMode } from '@/features/orders/utils/assign-rider';
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';
import { getProducts } from '@/features/products/api/get-products';
import type { ProductDisplay } from '@/features/products/types';

interface Rider {
  id: string;
  name: string;
  phone: string;
  status: string;
  is_active: boolean;
}

interface Branch {
  id: string;
  name: string;
  address?: string;
  is_main?: boolean;
  is_active?: boolean;
}

function OrdersPageContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { logout, user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [riders, setRiders] = useState<Rider[]>([]);
  const [showRiderDialog, setShowRiderDialog] = useState(false);
  const [selectedRiderId, setSelectedRiderId] = useState<string>('');
  const [showCreateOrderDialog, setShowCreateOrderDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [branchFilter, setBranchFilter] = useState('ALL');
  const [dateFilter, setDateFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState('date_desc');

  const [products, setProducts] = useState<ProductDisplay[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [ordersNotice, setOrdersNotice] = useState<string | null>(null);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [branchesError, setBranchesError] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState({
    customer_name: '',
    customer_phone: '',
    delivery_address: '',
    delivery_notes: '',
    branch_id: '',
  });
  const [draftItem, setDraftItem] = useState({
    productId: '',
    variantId: '',
    quantity: 1,
  });
  
  // Editing state for OrderCard
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    customer_name: '',
    customer_phone: '',
    delivery_address: '',
    delivery_notes: ''
  });

  const statusTabs = [
    { value: 'ALL', label: 'All Orders', statuses: [] as Order['status'][] },
    {
      value: 'PENDING',
      label: 'Pending',
      statuses: [
        'ORDER_SUBMITTED',
        'CONFIRMED_BY_MANAGER',
        'DELIVERY_REQUEST_STARTED',
        'RIDER_CONFIRMED_DIGITAL',
        'RIDER_CONFIRMED_MANUAL',
      ] as Order['status'][],
    },
    { value: 'NEW', label: 'New', statuses: ['ORDER_SUBMITTED'] as Order['status'][] },
    { value: 'PREPARING', label: 'Preparing', statuses: ['CONFIRMED_BY_MANAGER'] as Order['status'][] },
    {
      value: 'READY',
      label: 'Ready to Dispatch',
      statuses: ['DELIVERY_REQUEST_STARTED', 'RIDER_CONFIRMED_DIGITAL', 'RIDER_CONFIRMED_MANUAL'] as Order['status'][],
    },
    { value: 'ON_THE_WAY', label: 'On the Way', statuses: ['OUT_FOR_DELIVERY'] as Order['status'][] },
    { value: 'COMPLETED', label: 'Completed', statuses: ['DELIVERED'] as Order['status'][] },
    { value: 'CANCELLED', label: 'Cancelled', statuses: ['CANCELLED'] as Order['status'][] },
  ] as const;

  const normalizeStatusFilter = (raw: string) => {
    const normalized = raw.trim().toUpperCase();
    const aliasMap: Record<string, string> = {
      OUT_FOR_DELIVERY: 'ON_THE_WAY',
      DELIVERED: 'COMPLETED',
      ORDER_SUBMITTED: 'NEW',
    };

    const resolved = aliasMap[normalized] || normalized;
    return statusTabs.some((tab) => tab.value === resolved) ? resolved : 'ALL';
  };

  const normalizeDateFilter = (raw: string) => {
    const normalized = raw.trim().toUpperCase();
    return ['ALL', 'TODAY', '7D', '30D'].includes(normalized) ? normalized : 'ALL';
  };

  const updateUrlParams = (updates: { status?: string; date?: string }) => {
    const next = new URLSearchParams(searchParams);

    if (updates.status !== undefined) {
      const normalized = normalizeStatusFilter(updates.status);
      if (!normalized || normalized === 'ALL') {
        next.delete('status');
      } else {
        next.set('status', normalized);
      }
    }

    if (updates.date !== undefined) {
      const normalized = normalizeDateFilter(updates.date);
      if (!normalized || normalized === 'ALL') {
        next.delete('date');
      } else {
        next.set('date', normalized);
      }
    }

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  };

  useEffect(() => {
    const statusParam = searchParams.get('status');
    if (statusParam) {
      setStatusFilter(normalizeStatusFilter(statusParam));
    }
    const dateParam = searchParams.get('date');
    if (dateParam) {
      setDateFilter(normalizeDateFilter(dateParam));
    }
  }, [searchParams]);

  const matchesStatusFilter = (order: Order, filter: string) => {
    if (filter === 'ALL') return true;
    const tab = statusTabs.find((entry) => entry.value === filter);
    return tab ? tab.statuses.includes(order.status) : order.status === filter;
  };

  const nonStatusFilteredOrders = useMemo(() => {
    const normalizedTerm = searchTerm.trim().toLowerCase();
    const today = new Date().toISOString().slice(0, 10);
    const matchesDateFilter = (dateStr: string) => {
      if (!dateStr) return false;
      const created = new Date(dateStr);
      if (Number.isNaN(created.getTime())) return false;
      if (dateFilter === 'TODAY') return created.toISOString().slice(0, 10) === today;
      if (dateFilter === '7D') return created.getTime() >= Date.now() - 7 * 86400000;
      if (dateFilter === '30D') return created.getTime() >= Date.now() - 30 * 86400000;
      return true;
    };

    let filtered = [...orders];
    if (branchFilter !== 'ALL') {
      filtered = filtered.filter((order) => order.branch_id === branchFilter);
    }
    if (dateFilter !== 'ALL') {
      filtered = filtered.filter((order) => matchesDateFilter(order.created_at));
    }
    if (normalizedTerm) {
      filtered = filtered.filter((order) => {
        const items = order.items || [];
        const haystack = [
          order.id,
          order.order_number ? String(order.order_number) : '',
          order.order_reference,
          order.customer_phone,
          order.customer_name,
          order.delivery_address,
          order.delivery_notes,
          order.status,
          order.current_rider_name,
          ...items.map((item) => `${item.product_name} ${item.variant || ''}`)
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(normalizedTerm);
      });
    }

    return filtered;
  }, [orders, searchTerm, branchFilter, dateFilter]);

  const filteredOrders = useMemo(() => {
    const filtered = nonStatusFilteredOrders.filter((order) => matchesStatusFilter(order, statusFilter));

    filtered.sort((a, b) => {
      if (sortBy === 'date_asc') return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      if (sortBy === 'total_desc') return (b.total_amount || 0) - (a.total_amount || 0);
      if (sortBy === 'total_asc') return (a.total_amount || 0) - (b.total_amount || 0);
      if (sortBy === 'customer_asc') {
        const nameA = (a.customer_name || a.customer_phone || '').toLowerCase();
        const nameB = (b.customer_name || b.customer_phone || '').toLowerCase();
        return nameA.localeCompare(nameB);
      }
      if (sortBy === 'status_asc') {
        return (a.status || '').localeCompare(b.status || '');
      }
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    return filtered;
  }, [nonStatusFilteredOrders, sortBy, statusFilter]);

  const groupedOrders = useMemo(() => {
    if (statusFilter !== 'ALL') return [];
    const groups: { status: string; label: string; orders: Order[] }[] = [];
    statusTabs
      .filter((tab) => tab.value !== 'ALL')
      .forEach((tab) => {
        const list = filteredOrders.filter((order) => matchesStatusFilter(order, tab.value));
        if (list.length) {
          groups.push({ status: tab.value, label: tab.label, orders: list });
        }
      });
    const remaining = filteredOrders.filter(
      (order) =>
        !statusTabs
          .filter((tab) => tab.value !== 'ALL')
          .some((tab) => matchesStatusFilter(order, tab.value))
    );
    if (remaining.length) {
      groups.push({ status: 'OTHER', label: 'Other', orders: remaining });
    }
    return groups;
  }, [filteredOrders, statusFilter]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('ALL');
    setBranchFilter('ALL');
    setDateFilter('ALL');
    setSortBy('date_desc');
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllOrders();
      setOrders(data);
    } catch (err) {
      setError('Failed to load orders');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRiders = async () => {
    try {
      if (!supabase) throw new Error('Supabase not configured');
      const { data, error } = await supabase
        .from('riders')
        .select('*')
        .eq('is_active', true);
      
      if (error) throw error;
      
      const mappedRiders: Rider[] = (data || [])
        .map((r: any) => ({
        id: r.id,
        name: r.full_name || r.name || 'Unnamed Rider',
        phone: r.phone || '',
        status: r.status || 'AVAILABLE',
        is_active: r.is_active
      }))
        .sort((left, right) => left.name.localeCompare(right.name));
      setRiders(mappedRiders);
      return mappedRiders;
    } catch (err: any) {
      console.error('Failed to load riders:', err);
      const fallbackRiders = [
        { id: '550e8400-e29b-41d4-a716-446655440000', name: 'John Rider', phone: '+254798765432', status: 'AVAILABLE', is_active: true },
      ];
      setRiders(fallbackRiders);
      return fallbackRiders;
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchRiders();
    
    // Adaptive polling: faster for active orders, slower for completed
    const POLLING_INTERVAL = 15000; // 15 seconds - faster than before
    const interval = setInterval(fetchOrders, POLLING_INTERVAL);
    
    // Refresh on window focus for immediate updates
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchOrders();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!ordersNotice) return;
    const timer = window.setTimeout(() => setOrdersNotice(null), 3500);
    return () => window.clearTimeout(timer);
  }, [ordersNotice]);

  const assignableRiders = useMemo(
    () => riders.filter((rider) => rider.is_active),
    [riders]
  );

  const fetchProducts = async () => {
    setProductsLoading(true);
    setProductsError(null);
    try {
      const result = await getProducts({ perPage: 200, inStock: true, sortBy: 'name_asc' });
      const list = result?.data || [];
      setProducts(list);
      if (list.length > 0) {
        const firstProduct = list[0];
        const firstVariant = firstProduct.variants?.[0];
        setDraftItem((prev) => ({
          productId: prev.productId || firstProduct.id,
          variantId: prev.variantId || (firstVariant ? firstVariant.id : ''),
          quantity: prev.quantity || 1,
        }));
      }
    } catch (err) {
      console.error('Failed to load products:', err);
      setProductsError('Failed to load products');
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchBranches = async () => {
    setBranchesLoading(true);
    setBranchesError(null);
    try {
      const { data, error } = await supabase
        .from('branches')
        .select('id, name, address, is_main, is_active')
        .eq('is_active', true)
        .order('is_main', { ascending: false })
        .order('name', { ascending: true });

      if (error) throw new Error(error.message || 'Failed to load branches');
      const sorted = (data || []).sort((a: Branch, b: Branch) => {
        if (a.is_main && !b.is_main) return -1;
        if (!a.is_main && b.is_main) return 1;
        return a.name.localeCompare(b.name);
      });
      setBranches(sorted);
      if (!createForm.branch_id && sorted.length > 0) {
        const preferred = user?.branchId && isUuid(user.branchId) ? user.branchId : '';
        const preferredExists = preferred ? sorted.some((b) => b.id === preferred) : false;
        setCreateForm((prev) => ({ ...prev, branch_id: preferredExists ? preferred : sorted[0].id }));
      }
    } catch (err) {
      console.error('Failed to load branches:', err);
      setBranchesError('Failed to load branches');
    } finally {
      setBranchesLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const isUuid = (value: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

  function getActorId(userId?: string): string {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (userId && uuidRegex.test(userId)) {
      return userId;
    }
    return '00000000-0000-0000-0000-000000000001';
  }

  const handleAction = async (action: OrderCardAction, order: Order) => {
    setActionLoading(order.id);
    try {
      switch (action) {
        case 'confirm':
          await updateOrderStatus(order.id, {
            status: 'CONFIRMED_BY_MANAGER',
            actor_type: 'admin',
            actor_id: getActorId(user?.id),
            expected_version: order.version,
          });
          break;
        case 'startDelivery':
          await updateOrderStatus(order.id, {
            status: 'DELIVERY_REQUEST_STARTED',
            actor_type: 'admin',
            actor_id: getActorId(user?.id),
            expected_version: order.version,
          });
          break;
        case 'sendOut':
          // Rider confirmed, now sending out for delivery
          await updateOrderStatus(order.id, {
            status: 'OUT_FOR_DELIVERY',
            actor_type: 'admin',
            actor_id: getActorId(user?.id),
            expected_version: order.version,
          });
          break;
        case 'confirmDelivery':
          await updateOrderStatus(order.id, {
            status: 'DELIVERED',
            actor_type: 'admin',
            actor_id: getActorId(user?.id),
            expected_version: order.version,
          });
          break;
        case 'cancel':
          await updateOrderStatus(order.id, {
            status: 'CANCELLED',
            actor_type: 'admin',
            actor_id: getActorId(user?.id),
            expected_version: order.version,
          });
          break;
        case 'delete':
          await deleteOrder(order.id, {
            actor_type: 'admin',
            actor_id: getActorId(user?.id),
            confirm: true,
          });
          break;
        case 'assignRider':
          setSelectedOrder(order);
          {
            const availableRiders = await fetchRiders();
            setSelectedRiderId((currentValue) => {
              if (availableRiders.length === 1) {
                return availableRiders[0]?.id || '';
              }
              if (availableRiders.some((rider) => rider.id === currentValue)) {
                return currentValue;
              }
              return '';
            });
          }
          setShowRiderDialog(true);
          setActionLoading(null);
          return;
      }
      await fetchOrders();
    } catch (err) {
      alert(`Failed to ${action}: ` + (err as Error).message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmAssignRider = async (mode: AssignRiderMode) => {
    if (!selectedOrder || !selectedRiderId) {
      alert('Please select a rider');
      return;
    }
    
    setShowRiderDialog(false);
    setActionLoading(selectedOrder.id);
    
    try {
      await assignRider(
        selectedOrder.id,
        buildAssignRiderRequest(selectedOrder, selectedRiderId, getActorId(user?.id), mode)
      );
      await fetchOrders();
    } catch (err) {
      alert('Failed to assign rider: ' + (err as Error).message);
    } finally {
      setActionLoading(null);
      setSelectedOrder(null);
      setSelectedRiderId('');
    }
  };

  const selectedProduct = products.find((p) => p.id === draftItem.productId);
  const selectedVariants = selectedProduct?.variants || [];
  const selectedVariant = selectedVariants.find((v) => v.id === draftItem.variantId) || selectedVariants[0];

  const handleAddItem = () => {
    setCreateError(null);
    setCreateSuccess(null);
    if (!selectedProduct) {
      setCreateError('Please select a product.');
      return;
    }
    if (!selectedVariant) {
      setCreateError('Please select a product variant.');
      return;
    }
    if (draftItem.quantity < 1) {
      setCreateError('Quantity must be at least 1.');
      return;
    }

    const newItem: OrderItem = {
      product_id: selectedProduct.id,
      product_name: selectedProduct.name,
      quantity: draftItem.quantity,
      unit_price: selectedVariant.price,
      variant: selectedVariant.volume,
    };

    setOrderItems((prev) => [...prev, newItem]);
  };

  const handleRemoveItem = (index: number) => {
    setOrderItems((prev) => prev.filter((_, i) => i !== index));
  };

  const orderTotal = orderItems.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);

  const handleCreateOrder = async () => {
    setCreateError(null);
    setCreateSuccess(null);
    if (orderItems.length === 0) {
      setCreateError('Add at least one item.');
      return;
    }

    setCreateLoading(true);
    try {
      await createAdminOrder({
        customer_phone: createForm.customer_phone.trim() || null,
        customer_name: createForm.customer_name.trim() || null,
        delivery_address: createForm.delivery_address.trim() || null,
        delivery_notes: createForm.delivery_notes.trim() || null,
        branch_id: createForm.branch_id || null,
        items: orderItems,
        total: orderTotal,
        trigger_source: 'call_button',
      });
      setCreateSuccess('Order created successfully.');
      setOrderItems([]);
      setCreateForm({ customer_name: '', customer_phone: '', delivery_address: '', delivery_notes: '', branch_id: createForm.branch_id });
      await fetchOrders();
      // Close the dialog after successful creation
      setShowCreateOrderDialog(false);
      setOrdersNotice('Order saved. Showing updated order list.');
      setStatusFilter('ALL');
      setSortBy('date_desc');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setCreateError((err as Error).message || 'Failed to create order.');
    } finally {
      setCreateLoading(false);
    }
  };

  // Edit handlers for OrderCard
  const handleStartEdit = (order: Order) => {
    setEditingOrderId(order.id);
    setEditForm({
      customer_name: order.customer_name || '',
      customer_phone: isPendingCall(order.customer_phone) ? '' : (order.customer_phone || ''),
      delivery_address: isPendingCall(order.delivery_address) ? '' : (order.delivery_address || ''),
      delivery_notes: order.delivery_notes || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingOrderId(null);
    setEditForm({ customer_name: '', customer_phone: '', delivery_address: '', delivery_notes: '' });
  };

  const handleSaveEdit = async () => {
    if (!editingOrderId) return;
    
    const order = orders.find(o => o.id === editingOrderId);
    if (!order) return;

    try {
      const normalizedOriginal = {
        customer_name: order.customer_name || '',
        customer_phone: isPendingCall(order.customer_phone) ? '' : (order.customer_phone || ''),
        delivery_address: isPendingCall(order.delivery_address) ? '' : (order.delivery_address || ''),
        delivery_notes: order.delivery_notes || '',
      };
      const normalizedEdits = {
        customer_name: editForm.customer_name.trim(),
        customer_phone: editForm.customer_phone?.trim() || '',
        delivery_address: editForm.delivery_address.trim(),
        delivery_notes: editForm.delivery_notes.trim(),
      };

      const hasChanges = Object.keys(normalizedEdits).some((key) => {
        const field = key as keyof typeof normalizedEdits;
        return normalizedEdits[field] !== normalizedOriginal[field];
      });

      if (hasChanges) {
        const payload: Record<string, unknown> = {
          actor_type: 'admin',
          actor_id: getActorId(user?.id),
        };

        if (normalizedEdits.customer_name) {
          payload.customer_name = normalizedEdits.customer_name;
        }
        if (normalizedEdits.customer_phone !== '') {
          payload.customer_phone = normalizedEdits.customer_phone;
        } else if (normalizedOriginal.customer_phone !== '') {
          payload.customer_phone = '';
        }
        if (normalizedEdits.delivery_address) {
          payload.delivery_address = normalizedEdits.delivery_address;
        }
        if (normalizedEdits.delivery_notes !== '' || normalizedOriginal.delivery_notes !== '') {
          payload.delivery_notes = normalizedEdits.delivery_notes;
        }

        await updateOrderDetails(editingOrderId, payload);
        await fetchOrders();
      }
      
      setEditingOrderId(null);
      setEditForm({ customer_name: '', customer_phone: '', delivery_address: '', delivery_notes: '' });
    } catch (error) {
      console.error('Failed to save order details:', error);
      setEditingOrderId(null);
    }
  };

  const handleFormChange = (field: string, value: string) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-brand-50">
      {/* Header */}
      <header className="bg-white border-b border-brand-100 sticky top-0 z-10">
        <Container>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between h-auto sm:h-16 py-3 sm:py-0 gap-2 sm:gap-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-4">
                <h1 className="text-lg sm:text-xl font-display font-bold text-brand-900">
                  Order Management
                </h1>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchOrders}
                  disabled={loading}
                  className="hidden sm:flex"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchOrders}
                disabled={loading}
                className="sm:hidden p-2"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 overflow-x-auto">
              <Button 
                size="sm" 
                onClick={() => setShowCreateOrderDialog(true)}
                className="bg-green-600 hover:bg-green-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Create Order</span>
                <span className="sm:hidden">+ New</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="whitespace-nowrap text-sm">
                Dashboard
              </Button>
              <span className="text-sm text-brand-600 hidden sm:inline">{user?.name}</span>
              <Button variant="ghost" size="sm" onClick={logout} className="whitespace-nowrap">
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </Container>
      </header>

      <Container className="py-4 sm:py-6 px-2 sm:px-4">
        {error && (
          <div className="bg-danger-50 border border-danger-200 rounded-lg p-3 sm:p-4 mb-4 flex items-center gap-2 text-danger-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}
        {ordersNotice && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 mb-4 text-green-700 text-sm">
            {ordersNotice}
          </div>
        )}

        <Card className="mb-4">
          <CardContent className="p-3">
            <div className="flex flex-wrap gap-2">
              {statusTabs.map((tab) => {
                const count = tab.value === 'ALL'
                  ? nonStatusFilteredOrders.length
                  : nonStatusFilteredOrders.filter((order) => matchesStatusFilter(order, tab.value)).length;
                const isActive = statusFilter === tab.value;
                return (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => {
                      setStatusFilter(tab.value);
                      updateUrlParams({ status: tab.value });
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                      isActive
                        ? 'bg-brand-700 text-white border-brand-700'
                        : 'bg-white text-brand-700 border-brand-200 hover:border-brand-400'
                    }`}
                  >
                    {tab.label} ({count})
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <div className="lg:col-span-2">
                <label className="text-xs font-medium text-brand-700">Search</label>
                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="mt-1 w-full rounded-md border border-brand-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                  placeholder="Order number, phone, customer, product"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-brand-700">Branch</label>
                <select
                  value={branchFilter}
                  onChange={(e) => setBranchFilter(e.target.value)}
                  className="mt-1 w-full rounded-md border border-brand-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                  disabled={branchesLoading || branches.length === 0}
                >
                  <option value="ALL">All branches</option>
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-brand-700">Date range</label>
                <select
                  value={dateFilter}
                  onChange={(e) => {
                    setDateFilter(e.target.value);
                    updateUrlParams({ date: e.target.value });
                  }}
                  className="mt-1 w-full rounded-md border border-brand-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                >
                  <option value="ALL">All time</option>
                  <option value="TODAY">Today</option>
                  <option value="7D">Last 7 days</option>
                  <option value="30D">Last 30 days</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-brand-700">Sort</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="mt-1 w-full rounded-md border border-brand-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                >
                  <option value="date_desc">Newest first</option>
                  <option value="date_asc">Oldest first</option>
                  <option value="total_desc">Highest total</option>
                  <option value="total_asc">Lowest total</option>
                  <option value="customer_asc">Customer A-Z</option>
                  <option value="status_asc">Status</option>
                </select>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-brand-600">
              <span>
                Showing {filteredOrders.length} of {orders.length} orders
              </span>
              <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                Clear filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Orders List */}
        <div className="space-y-3 sm:space-y-4">
          {orders.length === 0 && !loading ? (
            <Card>
              <CardContent className="p-6 sm:p-8 text-center text-brand-500">
                <ShoppingCart className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4 opacity-50" />
                <p>No orders found</p>
              </CardContent>
            </Card>
          ) : filteredOrders.length === 0 && !loading ? (
            <Card>
              <CardContent className="p-6 sm:p-8 text-center text-brand-500">
                <ShoppingCart className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4 opacity-50" />
                <p>No orders match your filters</p>
              </CardContent>
            </Card>
          ) : (
            statusFilter === 'ALL' ? (
              groupedOrders.map((group) => (
                <div key={group.status} className="space-y-3">
                  <div className="flex items-center justify-between text-xs font-semibold text-brand-700 uppercase tracking-wide px-1">
                    <span>{group.label}</span>
                    <span>{group.orders.length}</span>
                  </div>
                  {group.orders.map((order) => (
                    <OrderCard
                      key={order.id}
                      order={order}
                      riders={riders}
                      isExpanded={expandedOrder === order.id}
                      onToggleExpand={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                      isEditing={editingOrderId === order.id}
                      editForm={editForm}
                      onStartEdit={() => handleStartEdit(order)}
                      onCancelEdit={handleCancelEdit}
                      onSaveEdit={handleSaveEdit}
                      onFormChange={handleFormChange}
                      actionLoading={actionLoading === order.id}
                      onAction={(action) => handleAction(action, order)}
                    />
                  ))}
                </div>
              ))
            ) : (
              filteredOrders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  riders={riders}
                  isExpanded={expandedOrder === order.id}
                  onToggleExpand={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                  isEditing={editingOrderId === order.id}
                  editForm={editForm}
                  onStartEdit={() => handleStartEdit(order)}
                  onCancelEdit={handleCancelEdit}
                  onSaveEdit={handleSaveEdit}
                  onFormChange={handleFormChange}
                  actionLoading={actionLoading === order.id}
                  onAction={(action) => handleAction(action, order)}
                />
              ))
            )
          )}
        </div>
      </Container>

      {/* Create Order Dialog */}
      <Dialog open={showCreateOrderDialog} onOpenChange={setShowCreateOrderDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <h2 className="text-xl font-semibold">Create New Order</h2>
            <p className="text-sm text-brand-600">Take phone or walk-in orders and add items manually.</p>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {productsError && (
              <div className="bg-danger-50 border border-danger-200 rounded-lg p-3 text-danger-700 text-sm">
                {productsError}
              </div>
            )}

            {branchesError && (
              <div className="bg-danger-50 border border-danger-200 rounded-lg p-3 text-danger-700 text-sm">
                {branchesError}
              </div>
            )}

            {createError && (
              <div className="bg-danger-50 border border-danger-200 rounded-lg p-3 text-danger-700 text-sm">
                {createError}
              </div>
            )}

            {createSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-700 text-sm">
                {createSuccess}
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-brand-800">Branch</label>
                <select
                  value={createForm.branch_id}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, branch_id: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-brand-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                  disabled={branchesLoading || branches.length === 0}
                >
                  {branches.length === 0 ? (
                    <option value="">No branches available</option>
                  ) : (
                    branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-brand-800">Customer Name</label>
                <input
                  value={createForm.customer_name}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, customer_name: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-brand-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-brand-800">Customer Phone</label>
                <input
                  value={createForm.customer_phone}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, customer_phone: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-brand-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                  placeholder="+254..."
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-brand-800">Delivery Address</label>
                <input
                  value={createForm.delivery_address}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, delivery_address: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-brand-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                  placeholder="Address"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-brand-800">Delivery Notes</label>
                <input
                  value={createForm.delivery_notes}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, delivery_notes: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-brand-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-4">
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-brand-800">Product</label>
                <select
                  value={draftItem.productId}
                  onChange={(e) => {
                    const productId = e.target.value;
                    const product = products.find((p) => p.id === productId);
                    const variant = product?.variants?.[0];
                    setDraftItem((prev) => ({
                      ...prev,
                      productId,
                      variantId: variant ? variant.id : '',
                    }));
                  }}
                  className="mt-1 w-full rounded-md border border-brand-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                  disabled={productsLoading || products.length === 0}
                >
                  {products.length === 0 ? (
                    <option value="">No products available</option>
                  ) : (
                    products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-brand-800">Variant</label>
                <select
                  value={draftItem.variantId}
                  onChange={(e) => setDraftItem((prev) => ({ ...prev, variantId: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-brand-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                  disabled={productsLoading || selectedVariants.length === 0}
                >
                  {selectedVariants.length === 0 ? (
                    <option value="">No variants</option>
                  ) : (
                    selectedVariants.map((variant) => (
                      <option key={variant.id} value={variant.id}>
                        {variant.volume} - KES {variant.price.toLocaleString()}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-brand-800">Qty</label>
                <input
                  type="number"
                  min={1}
                  value={draftItem.quantity}
                  onChange={(e) => setDraftItem((prev) => ({ ...prev, quantity: Number(e.target.value) }))}
                  className="mt-1 w-full rounded-md border border-brand-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <Button onClick={handleAddItem} disabled={productsLoading || products.length === 0}>
                Add Item
              </Button>
              <div className="text-sm text-brand-700 font-medium">
                Total: KES {orderTotal.toLocaleString()}
              </div>
            </div>

            <div className="border border-brand-100 rounded-lg overflow-hidden">
              <div className="grid grid-cols-12 bg-brand-50 px-3 py-2 text-xs font-semibold text-brand-700">
                <div className="col-span-5">Item</div>
                <div className="col-span-2 text-right">Qty</div>
                <div className="col-span-3 text-right">Unit</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              {orderItems.length === 0 ? (
                <div className="px-3 py-4 text-sm text-brand-500">No items added yet.</div>
              ) : (
                orderItems.map((item, index) => (
                  <div key={`${item.product_id}-${index}`} className="grid grid-cols-12 items-center px-3 py-2 border-t text-sm">
                    <div className="col-span-5">
                      {item.product_name} {item.variant ? `(${item.variant})` : ''}
                    </div>
                    <div className="col-span-2 text-right">{item.quantity}</div>
                    <div className="col-span-3 text-right">KES {item.unit_price.toLocaleString()}</div>
                    <div className="col-span-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveItem(index)}>
                        Remove
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowCreateOrderDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateOrder} disabled={createLoading || orderItems.length === 0}>
                {createLoading ? 'Creating...' : 'Create Order'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rider Selection Dialog */}
      <Dialog
        open={showRiderDialog}
        onOpenChange={(open) => {
          setShowRiderDialog(open);
          if (!open) {
            setSelectedOrder(null);
            setSelectedRiderId('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <h3 className="text-lg font-semibold">Dispatch Order</h3>
          </DialogHeader>
          <p className="text-sm text-gray-600 mb-4">
            Assign a rider for order: #{selectedOrder?.id?.slice(-6)}. You can either save the assignment or assign and move the order straight to out for delivery.
          </p>
          <select
            value={selectedRiderId}
            onChange={(e) => setSelectedRiderId(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-base sm:text-sm"
          >
            <option value="">Select a rider...</option>
            {assignableRiders.map((rider) => (
              <option key={rider.id} value={rider.id}>
                {rider.name} - {rider.phone}
              </option>
            ))}
          </select>
          {assignableRiders.length === 0 && (
            <p className="mt-3 text-sm text-amber-700">
              No active riders are available right now. Add or re-activate a rider before dispatching this order.
            </p>
          )}
          <div className="flex gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowRiderDialog(false)} className="flex-1">
              Cancel
            </Button>
            <Button 
              variant="outline"
              onClick={() => handleConfirmAssignRider('assign_only')} 
              disabled={!selectedRiderId}
              className="flex-1"
            >
              Assign Only
            </Button>
            <Button 
              onClick={() => handleConfirmAssignRider('assign_and_send_out')} 
              disabled={!selectedRiderId}
              className="flex-1"
            >
              Assign & Send Out
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminOrdersPage() {
  return (
    <AuthGuard>
      <OrdersPageContent />
    </AuthGuard>
  );
}
